<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div class="d-flex justify-end px-4 pt-3">
        <v-btn small color="primary" outlined @click="toggleDisplayUnit">
          單位: {{ displayUnit === "hours" ? "小時" : "天" }}
          <v-icon small class="ml-1">mdi-swap-horizontal</v-icon>
        </v-btn>
      </div>

      <!-- Search Bar -->
      <div class="px-4 pt-4 pb-2" v-if="!targetUserId">
        <v-row class="align-center">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedUserId"
              :items="users"
              :loading="loadingUsers"
              item-text="name"
              item-value="id"
              placeholder="請輸入姓名"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-magnify"
              @change="onUserChange"
            >
              <template v-slot:label>
                <span class="red--text">搜尋員工</span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.job_title }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </div>

      <!-- Leave Balance Summary -->
      <div v-if="leaveBalance" class="px-4 py-4">
        <v-row>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3 text-center">
              <div class="text-subtitle-2 grey--text">特休假 (剩餘/總計)</div>
              <div class="text-h6 primary--text font-weight-bold">
                {{ formatDuration(leaveBalance.annual_leave) }} /
                {{ formatDuration(leaveBalance.annual_leave_max) }}
                {{ unitText }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3 text-center">
              <div class="text-subtitle-2 grey--text">事假 (剩餘/總計)</div>
              <div class="text-h6 orange--text font-weight-bold">
                {{ formatDuration(leaveBalance.personal_leave) }} /
                {{ formatDuration(leaveBalance.personal_leave_max) }}
                {{ unitText }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3 text-center">
              <div class="text-subtitle-2 grey--text">病假 (剩餘/總計)</div>
              <div class="text-h6 success--text font-weight-bold">
                {{ formatDuration(leaveBalance.sick_leave) }} /
                {{ formatDuration(leaveBalance.sick_leave_max) }} {{ unitText }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Data Table -->
      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">員工姓名</th>
              <th class="text-left">假別</th>
              <th class="text-left">開始時間</th>
              <th class="text-left">結束時間</th>
              <th class="text-left">請假({{ unitText }})</th>
              <th class="text-left">事由</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in records" :key="item.id">
              <td>{{ item.user ? item.user.name : "-" }}</td>
              <td>
                {{ item.leaveTypeInfo ? item.leaveTypeInfo.name : "未知假別" }}
              </td>
              <td>{{ formatTime(item.start_time) }}</td>
              <td>{{ formatTime(item.end_time) }}</td>
              <td>{{ formatDuration(item.leave_minutes) }}</td>
              <td>{{ item.reason || "-" }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div v-if="!selectedUserId" class="text-center py-8 grey--text">
        請選擇員工以查看紀錄
      </div>

      <div
        v-else-if="loading && records.length === 0"
        class="d-flex justify-center py-4"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>

      <div
        v-else-if="!loading && records.length === 0"
        class="text-center py-8 grey--text"
      >
        沒有資料
      </div>

      <div class="text-center my-4">
        <v-pagination
          v-model="page"
          :length="pageCount"
          @input="fetchRecords"
          prev-icon="fa fa-angle-left"
          next-icon="fa fa-angle-right"
          color="#cb0c9f"
          circle
        ></v-pagination>
      </div>
    </v-card-text>
    <info-dialog
      :visible.sync="showInfoDialog"
      :title="infoDialogTitle"
      :message="infoDialogMessage"
    />
  </v-card>
</template>

<script>
import InfoDialog from "@/components/Common/InfoDialog.vue";
import { formatDateTime } from "@/utils/time";

export default {
  name: "EmployeeLeaveHistoryWidget",
  components: {
    InfoDialog,
  },
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    targetUserId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      users: [],
      selectedUserId: null,
      records: [],
      page: 1,
      itemsPerPage: 15,
      totalItems: 0,
      loadingUsers: false,
      loading: false,
      fetchError: false,
      showInfoDialog: false,
      infoDialogTitle: "",
      infoDialogMessage: "",
      totalPages: 1,
      leaveBalance: null,
      displayUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  computed: {
    pageCount() {
      return this.totalPages || 1;
    },
    unitText() {
      return this.displayUnit === "hours" ? "小時" : "天";
    },
  },
  mounted() {
    if (this.targetUserId) {
      this.selectedUserId = this.targetUserId;
      console.log("mounted", this.selectedUserId);
      this.fetchRecords();
    }
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  watch: {
    selectedCompany: {
      handler() {
        if (!this.targetUserId) {
          this.selectedUserId = null;
          this.records = [];
          this.fetchUsers();
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    formatTime(val) {
      return formatDateTime(val);
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null;
        if (!companyId) {
          this.users = [];
          return;
        }
        const params = new URLSearchParams({
          company_id: companyId,
        });
        const response = await this.$axios.get(`/api/users?${params}`);
        const payload = response.data;
        this.users = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.data)
          ? payload.data
          : [];
      } catch (error) {
        console.error("Fetch users error:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
    onUserChange() {
      this.page = 1;
      this.records = [];
      if (this.selectedUserId) {
        console.log("onUserChange", this.selectedUserId);
        this.fetchRecords();
      }
    },
    async fetchRecords() {
      this.loading = true;
      this.records = [];
      this.fetchError = false;

      try {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null;
        if (!companyId || !this.selectedUserId) {
          console.log("no company or user id");
          console.log("companyId", companyId);
          console.log("selectedUserId", this.selectedUserId);
          return;
        }

        const params = new URLSearchParams({
          page: this.page,
          limit: this.itemsPerPage,
          company_id: companyId,
          user_id: this.selectedUserId,
          approval_status: "審核通過",
        });

        const response = await this.$axios.get(
          `/api/leave-records/user?${params}`
        );
        const data = response.data;

        const newItems = data.data || [];
        const pagination = data.pagination || {};
        this.leaveBalance = data.leaveBalance || null;

        this.totalItems = pagination.totalItems || 0;
        this.totalPages = pagination.totalPages || 1;

        this.records = newItems;
      } catch (error) {
        console.error("Fetch employee leave history error:", error);
        this.fetchError = true;
        this.showInfoDialog = true;
        this.infoDialogTitle = "Error";
        this.infoDialogMessage = "無法取得資料";
      } finally {
        this.loading = false;
      }
    },
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return "0";
      if (this.displayUnit === "hours") {
        const hours = minutes / 60;
        return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
      }
      const days = minutes / 480;
      return Number.isInteger(days) ? days.toString() : days.toFixed(1);
    },
    syncDisplayUnit() {
      this.displayUnit = localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDisplayUnit() {
      this.displayUnit = this.displayUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.displayUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    refresh() {
      if (this.selectedUserId) {
        this.page = 1;
        this.fetchRecords();
      }
    },
  },
};
</script>

<style scoped></style>
